import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, Tooltip, Fade, useMediaQuery, useTheme, Tab, Tabs } from '@mui/material';
import { MessageCircle } from 'lucide-react';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import type { Message, WidgetState } from './types/chat.types';
import { useSSEStream } from './hooks/useSSEStream';
import ChatHeader from './components/ChatHeader';
import ChatMessageList from './components/ChatMessageList';
import ChatInput from './components/ChatInput';
import VisualizationPanel from './components/VisualizationPanel';

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  execute_sql_query: 'Querying database',
  search_knowledge: 'Searching documentation',
  get_entity_hub: 'Opening knowledge hub',
  get_card: 'Opening knowledge card',
  follow_links: 'Following knowledge links',
  get_health_trends: 'Analyzing health trends',
  read_flight_data: 'Reading flight data',
  generate_report: 'Generating report',
  query_monday: 'Checking Monday.com',
};

const ChatWidget: React.FC = () => {
  const { showError, showSuccess } = useErrorHandler();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [widgetState, setWidgetState] = useState<WidgetState>('closed');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('Thinking...');
  const [isAnimating, setIsAnimating] = useState(false);
  // Managed-agent session ID, issued by the backend on first turn and echoed
  // back each follow-up request so the platform resumes server-side context.
  // Undefined on a fresh widget or after reset.
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [mobileTab, setMobileTab] = useState<0 | 1>(0);
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { startStream, cancel: cancelStream } = useSSEStream();

  // Escape key exits fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && widgetState === 'fullscreen') {
        setWidgetState('open');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [widgetState]);

  // Rotate loading status messages
  useEffect(() => {
    if (loading) {
      const statuses = ['Thinking...', 'Interpreting...', 'Analysing...'];
      let currentIndex = 0;
      setLoadingStatus(statuses[0]);

      statusIntervalRef.current = setInterval(() => {
        currentIndex = (currentIndex + 1) % statuses.length;
        setLoadingStatus(statuses[currentIndex]);
      }, 3000);

      return () => {
        if (statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
        }
      };
    }
  }, [loading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, []);

  // Stop animations when widget is closed
  useEffect(() => {
    if (widgetState === 'closed') {
      cancelStream();
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
      setLoading(false);
      setIsAnimating(false);
    }
  }, [widgetState, cancelStream]);

  const handleSendQuery = async () => {
    if (!query.trim()) return;

    // Stop any ongoing stream
    cancelStream();
    setIsAnimating(false);

    const userQuery = query.trim();
    const userMessage: Message = {
      type: 'user',
      content: userQuery,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    const assistantMessage: Message = {
      type: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    setIsAnimating(true);

    // Stop rotating status messages — SSE callbacks will set specific statuses
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }

    let firstTextReceived = false;

    await startStream(userQuery, sessionId, {
      onDelta: (text) => {
        if (!firstTextReceived) {
          firstTextReceived = true;
          setLoading(false);
        }
        setMessages((prev) => {
          const updated = [...prev];
          let lastAssistant = -1;
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].type === 'assistant') {
              lastAssistant = i;
              break;
            }
          }
          if (lastAssistant !== -1) {
            updated[lastAssistant] = {
              ...updated[lastAssistant],
              content: updated[lastAssistant].content + text,
            };
          }
          return updated;
        });
      },
      onToolStart: (tool, input) => {
        const displayName = TOOL_DISPLAY_NAMES[tool] || `Using ${tool.replace(/_/g, ' ')}`;
        const status = input ? `${displayName}: ${input}` : `${displayName}...`;
        setLoadingStatus(status);
      },
      onToolDone: (tool, durationMs) => {
        const displayName = TOOL_DISPLAY_NAMES[tool] || tool.replace(/_/g, ' ');
        const durationStr = durationMs ? ` (${(durationMs / 1000).toFixed(1)}s)` : '';
        setLoadingStatus(`${displayName} done${durationStr}. Formatting response...`);
      },
      onDone: (result, newSessionId) => {
        setMessages((prev) => {
          const updated = [...prev];
          let lastAssistant = -1;
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].type === 'assistant') {
              lastAssistant = i;
              break;
            }
          }
          if (lastAssistant !== -1) {
            updated[lastAssistant] = {
              ...updated[lastAssistant],
              queryResult: result,
            };
          }
          return updated;
        });
        if (newSessionId) {
          setSessionId(newSessionId);
        }
        setLoading(false);
        setIsAnimating(false);
      },
      onError: (message, code) => {
        setMessages((prev) => [
          ...prev.filter((m) => m !== assistantMessage),
          { type: 'error', content: message, timestamp: new Date() },
        ]);
        showError(new Error(message), 'Failed to process query');
        // Only reset the session when the backend signals the session is truly
        // dead. Transient errors (rate limits, tool failures, network blips)
        // leave the managed-agent session alive on Anthropic's side — clearing
        // sessionId on those silently drops prior-turn context.
        if (code === 'session_fatal') {
          setSessionId(undefined);
        }
        setLoading(false);
        setIsAnimating(false);
      },
    });
  };

  const handleResetConversation = useCallback(() => {
    cancelStream();
    setMessages([]);
    setQuery('');
    setLoading(false);
    setIsAnimating(false);
    setSessionId(undefined);
    showSuccess('Conversation reset');
  }, [showSuccess, cancelStream]);

  const handleToggleFullscreen = () => {
    setWidgetState((prev) => (prev === 'fullscreen' ? 'open' : 'fullscreen'));
  };

  const handleToggleMinimize = () => {
    setWidgetState((prev) => (prev === 'open' ? 'minimized' : 'open'));
  };

  // Check if there are visualizations to show
  const hasVisualizations = messages.some(
    (m) =>
      m.type === 'assistant' &&
      m.queryResult &&
      (m.queryResult.chart_specs?.length ||
        m.queryResult.health_trends ||
        m.queryResult.flight_data ||
        m.queryResult.sources?.length ||
        m.queryResult.generated_report ||
        (m.queryResult.results?.length && m.queryResult.sql_query))
  );

  // Floating button when closed
  if (widgetState === 'closed') {
    return (
      <Tooltip title="Ask AI Assistant (Beta)" placement="left">
        <Paper
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: 3,
            '&:hover': { boxShadow: 6, transform: 'scale(1.05)' },
            transition: 'all 0.2s',
            zIndex: 1300,
          }}
          onClick={() => setWidgetState('open')}
          data-testid="chat-widget-open"
        >
          <MessageCircle size={28} />
        </Paper>
      </Tooltip>
    );
  }

  // Fullscreen layout
  if (widgetState === 'fullscreen') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ChatHeader
          widgetState={widgetState}
          hasMessages={messages.length > 0}
          loading={loading}
          onToggleMinimize={handleToggleMinimize}
          onToggleFullscreen={handleToggleFullscreen}
          onClose={() => setWidgetState('closed')}
          onReset={handleResetConversation}
        />

        {isMobile ? (
          // Mobile: tabbed layout
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <Tabs
              value={mobileTab}
              onChange={(_, v) => setMobileTab(v as 0 | 1)}
              sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
            >
              <Tab label="Chat" />
              <Tab label="Visualizations" disabled={!hasVisualizations} />
            </Tabs>
            {mobileTab === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <ChatMessageList
                  messages={messages}
                  loading={loading}
                  loadingStatus={loadingStatus}
                  widgetState={widgetState}
                  onExampleClick={setQuery}
                />
                <ChatInput
                  query={query}
                  loading={loading}
                  isAnimating={isAnimating}
                  onQueryChange={setQuery}
                  onSend={handleSendQuery}
                />
              </Box>
            ) : (
              <VisualizationPanel messages={messages} loading={loading} compact={false} />
            )}
          </Box>
        ) : (
          // Desktop: split panel layout
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Left panel: Chat */}
            <Box
              sx={{
                width: '40%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <ChatMessageList
                messages={messages}
                loading={loading}
                loadingStatus={loadingStatus}
                widgetState={widgetState}
                onExampleClick={setQuery}
              />
              <ChatInput
                query={query}
                loading={loading}
                isAnimating={isAnimating}
                onQueryChange={setQuery}
                onSend={handleSendQuery}
              />
            </Box>

            {/* Right panel: Visualizations */}
            <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
              <VisualizationPanel messages={messages} loading={loading} compact={false} />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Normal widget (open/minimized)
  return (
    <Fade in={true}>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: widgetState === 'minimized' ? 320 : 420,
          height: widgetState === 'minimized' ? 'auto' : 600,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 6,
          borderRadius: 2,
          overflow: 'hidden',
          zIndex: 1300,
        }}
      >
        <ChatHeader
          widgetState={widgetState}
          hasMessages={messages.length > 0}
          loading={loading}
          onToggleMinimize={handleToggleMinimize}
          onToggleFullscreen={handleToggleFullscreen}
          onClose={() => setWidgetState('closed')}
          onReset={handleResetConversation}
        />

        {widgetState === 'open' && (
          <>
            <ChatMessageList
              messages={messages}
              loading={loading}
              loadingStatus={loadingStatus}
              widgetState={widgetState}
              onExampleClick={setQuery}
            />
            {/* Compact inline visualizations in normal widget mode */}
            {hasVisualizations && (
              <VisualizationPanel
                messages={messages}
                loading={loading}
                compact={true}
                onExpandFullscreen={handleToggleFullscreen}
              />
            )}
            <ChatInput
              query={query}
              loading={loading}
              isAnimating={isAnimating}
              onQueryChange={setQuery}
              onSend={handleSendQuery}
            />
          </>
        )}
      </Paper>
    </Fade>
  );
};

export default ChatWidget;
